import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AllocationRuleEditor } from '../Model';
import { AllocationSources } from '../AllocationSources';
import { DisbursementTargetPicker } from '../DisbursementTargetPicker';
import { BaseAllocationRuleModel } from './BaseAllocationRuleModel';
import { IMiniDataGridProps, MiniDataGridItem } from '../MiniDataGrid';
import { useDi, useDiMemo } from '@root/Services/DI';
import { FormatService } from '@root/Services/FormatService';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { Button, Drawer, Group, NumberInput, Select, Space, Stack, TextInput } from '@mantine/core';
import { DataGrid } from '@root/Components/DataGrid';
import { DataColumnConfig, DataGridState } from '@root/Components/DataGrid/Models';
import { exprBuilder, queryBuilder } from '@root/Services/QueryExpr';
import { IInvoiceRollup, IMonthlyRollup } from '@root/Services/Invoices/InvoiceSchemaService';
import { FundSource, FundSourceDefinition, FundSourceAmortizationAmortizationPeriodUnit } from '@apis/Invoices/model';
import { ExternalFundSource, LineItemFundSource } from '../../Models';
import { DatePicker } from '@mantine/dates';
import { CalendarTime } from 'tabler-icons-react';
import { SidePanel, SidePanelOpener, useSidePanelOpener } from '@root/Design/SidePanel';
import { EventEmitter, useEvent, useEventValue } from '@root/Services/EventEmitter';
import { useNavState } from '@root/Services/Router/NavState';
import { FillerSwitch } from '@root/Design/Filler';
import { CurrencyPreferenceService } from '@root/Services/Customers/CurrencyPreferenceService';

export const SharedCostRuleCard = observer(function SharedCostRuleCard({ ruleEditor }: { ruleEditor: AllocationRuleEditor }) {
    const sourcePicker = useSidePanelOpener();
    const model = useMemo(() => new SharedCostRuleModel(ruleEditor, sourcePicker), [ruleEditor.rule, sourcePicker]);

    const customPicker = useSidePanelOpener();
    const [disableDimension, setDisableDimension] = useState(false);

    const addSourceButtons = useMemo(
        () => [
            {
                label: 'Add Allocation Source',
                onClick: () => {
                    sourcePicker.open();
                },
            },
            {
                label: 'Add Custom Fee',
                onClick: () => {
                    customPicker.open();
                },
            },
        ],
        []
    );

    const addDisbursementButtons = useMemo(
        () => [
            {
                label: 'Add Filters',
                onClick: () => {},
            },
        ],
        []
    );

    // #region Mini Grid Related
    const getSources = useCallback(() => model.getSharedCostAllocationSources(), [model]);

    const sourceListProps = {
        nameHeader: 'Description',
        amountHeader: 'Amount',
        displayTotal: true,
    } as IMiniDataGridProps;

    // #endregion

    const handleCustomSave = useCallback(
        (
            name: string,
            description: string,
            amount: number,
            date: Date,
            amoritizationPeriodValue: number,
            amortizationPeriodUnit: FundSourceAmortizationAmortizationPeriodUnit
        ) => {
            model.addCustomFee(name, description, amount, date, amoritizationPeriodValue, amortizationPeriodUnit);
            ruleEditor.setFundSource({ Sources: model.getSources() } as FundSourceDefinition);
            customPicker.close();
        },
        []
    );

    useEffect(() => {
        setDisableDimension(model.getCustomSources().length > 0);
    }, [model.getCustomSources()]);

    return (
        <>
            <AllocationSources
                title="Shared Costs"
                gridData={getSources}
                gridProps={sourceListProps}
                buttons={addSourceButtons}
                sourceChanged={model.sourcesChanged}
                helpText={`Identify shared costs from the invoice or add custom fees that should be reallocated.`}
            />
            <DisbursementTargetPicker ruleEditor={ruleEditor} buttons={addDisbursementButtons} disableDimension={disableDimension} />
            <SharedCostOptionsDrawer model={model} sourcePicker={sourcePicker} />
            <CustomFeeDrawer setIsCustomOpen={customPicker} handleCustomSave={handleCustomSave} />
        </>
    );
});

type SharedCostProduct = { id: string; productName: string; lineItemType: string; cost?: number };
class SharedCostRuleModel extends BaseAllocationRuleModel {
    private productCosts?: Map<string, number>;
    private sharedCostProducts?: SharedCostProduct[];
    public loading = new EventEmitter<boolean>(true);
    private sourcePicker: SidePanelOpener;
    private initialSelection?: SharedCostProduct[];

    public constructor(ruleEditor: AllocationRuleEditor, sourcePicker: SidePanelOpener) {
        super(ruleEditor);
        this.sourcePicker = sourcePicker;
        this.loadSharedCostOptions();
    }

    public addCustomFee(
        name: string,
        description: string,
        amount: number,
        date: Date,
        amoritizationPeriodValue: number,
        amortizationPeriodUnit: FundSourceAmortizationAmortizationPeriodUnit
    ) {
        const customSource = this.createFundSource('External') as ExternalFundSource;
        customSource.Name = name;
        customSource.ExternalDetail = {
            Name: name,
            Description: description,
            Amount: amount,
            Date: date.toISOString(),
        };
        customSource.Amortization = {
            AmortizationPeriodUnit: amortizationPeriodUnit,
            AmortizationPeriodValue: amoritizationPeriodValue,
            Amount: amount,
            Date: date.toISOString(),
        };

        this.addSource(customSource);
        this.sourcesChanged.emit();
    }

    public getCustomSources(): FundSource[] {
        let sources = this.getSources();
        if (sources) {
            sources = sources.filter((s) => s.SourceType === 'External');
        }
        return sources;
    }

    public getSource(): FundSource {
        let source = this.getSources().find((s) => s.SourceType === 'LineItems');
        if (!source) {
            const lineItemSource = this.createFundSource('LineItems') as LineItemFundSource;

            this.addSource((source = lineItemSource as FundSource));
        }
        return source!;
    }

    public async loadSharedCostOptions() {
        this.loading.emit(true);
        try {
            if (!this.sharedCostProducts) {
                const currentOptions = await this.getSharedCostOptions();
                const previousSelections = this.getPersistedProducts();
                if (previousSelections.length > 0) {
                    this.sharedCostProducts = [...previousSelections.filter((p) => !currentOptions.some((o) => o.id === p.id)), ...currentOptions];
                    this.productCosts = currentOptions.reduce((result, item) => result.set(item.id, item.cost), new Map<string, number>());
                } else {
                    this.sharedCostProducts = [...currentOptions];
                    this.productCosts = currentOptions.reduce((result, item) => result.set(item.id, item.cost), new Map<string, number>());
                }
            }
            return this.sharedCostProducts.sort((a, b) => a.productName.localeCompare(b.productName))!;
        } finally {
            this.loading.emit(false);
            this.sourcesChanged.emit();
            this.consumeSubType(this.loadInitialSelection);
        }
    }

    private loadInitialSelection = (subType: string) => {
        this.initialSelection = this.filterProducts(
            this.sharedCostProducts ?? [],
            (item) => item.lineItemType.toLowerCase() === subType || (subType === 'support' && item.productName.toLowerCase().includes('support'))
        );
        if (this.initialSelection.length > 0) {
            this.sourcePicker.open();
        }
    };

    public clearInitial() {
        this.initialSelection = undefined;
    }

    public getProductOptions() {
        return this.loading.value ? [] : this.sharedCostProducts ?? [];
    }

    private async getSharedCostOptions() {
        const { allocDim, month, showbackSvc } = this.ruleEditor;

        const { chargeFees, chargeSupport, chargeNotMarketplace, discountCredits } = showbackSvc.getMetricCriteria(allocDim);
        const items = await queryBuilder<IMonthlyRollup>()
            .where((b) =>
                b.and(
                    b.fromExpr<boolean>(chargeNotMarketplace),
                    b.or(...[chargeSupport, chargeFees, discountCredits].map((c) => b.fromExpr<boolean>(c)))
                )
            )
            .select((b) => ({
                productName: b.model['product/ProductName']!,
                lineItemType: b.values(b.model.NativeLineItemType),
                cost: b.sum(b.model['lineItem/UnblendedCost']!),
            }))
            .execute((q) => showbackSvc.invoiceApi.queryMonthlyRollup(q, [month]))
            .then((r) => r.Results ?? []);

        return items.map((item) => ({
            ...item,
            id: JSON.stringify([item.productName, item.lineItemType]),
        }));
    }

    public getSharedCostAllocationSources() {
        const products = this.getSelectedProducts().map(
            (p) =>
                ({
                    getAmount: () => this.getProductCost(p) ?? 0,
                    getName: () => `(${p.lineItemType}) ${p.productName}`,
                    onChanged: this.sourcesChanged,
                    remove: () => this.removeProduct(p),
                } as MiniDataGridItem)
        );

        const sources = this.getCustomSources();
        const customNames = sources?.map(
            (m) =>
                ({
                    getAmount: () => m.ExternalDetail?.Amount,
                    getName: () => m.Name,
                    onChanged: this.sourcesChanged,
                    remove: () => this.removeSource(m),
                } as MiniDataGridItem)
        );

        return [...products, ...customNames];
    }

    public getProductCost(product: SharedCostProduct) {
        return this.productCosts?.get(product.id) ?? 0;
    }

    private getPersistedProducts() {
        const source = this.getSource();
        return (source.PresentationOptions?.['products'] ?? []) as SharedCostProduct[];
    }

    public getSelectedProducts() {
        return this.getPersistedProducts()
            .map((p) => this.sharedCostProducts?.find((sp) => sp.id === p.id))
            .filter((p) => !!p) as SharedCostProduct[];
    }

    public getOrCreateInitialSelection() {
        let items = this.getSelectedProducts();
        if (!items.length) {
            items = this.initialSelection ?? this.filterProducts(this.sharedCostProducts ?? []);
        }
        return items;
    }

    public setSelectedProducts(products: SharedCostProduct[]) {
        const source = this.getSource();
        source.PresentationOptions = { products };
        this.updateFilters();
        this.sourcesChanged.emit();
    }

    public getCustomNames() {
        const sources = this.getCustomSources();
        return sources.map((s) => s.Name) as string[];
    }

    private filterProducts(products: SharedCostProduct[], criteria?: (item: SharedCostProduct) => boolean) {
        return products.filter((item) => !!item.cost && criteria?.(item) !== false) ?? [];
    }

    private getProductNameFilters() {
        const { builder: xb } = exprBuilder<{ ['product/ProductName']: string; NativeLineItemType: string }>();

        const selections = this.getSelectedProducts();
        const groupedSelections = selections.reduce((res, item) => {
            let typeProducts = res.get(item.lineItemType);
            if (!typeProducts) {
                res.set(item.lineItemType, (typeProducts = []));
            }
            typeProducts?.push(item.productName);
            return res;
        }, new Map<string, string[]>());
        const litFilters = [...groupedSelections.entries()].map(([lit, productNames]) =>
            xb.and(xb.model['product/ProductName'].eq(productNames), xb.model.NativeLineItemType.eq(lit))
        );
        const filters = xb.or(...litFilters);

        return xb.resolve(filters);
    }

    private removeProduct(product: SharedCostProduct) {
        const products = this.getSelectedProducts();
        this.setSelectedProducts(products.filter((p) => p !== product));
    }

    private updateFilters() {
        const { allocDim, showbackSvc } = this.ruleEditor;
        const { chargeFees, chargeSupport } = showbackSvc.getMetricCriteria(allocDim);
        const productSource = this.getSource();
        const selections = this.getSelectedProducts();

        if (!selections.length) {
            productSource.Filters = {};
        } else {
            productSource.Filters = {
                InclusionRules: [
                    {
                        Name: 'Shared Cost Charges',
                        Filter: {
                            Operation: 'and',
                            Operands: [
                                this.getProductNameFilters(),
                                {
                                    Operation: 'or',
                                    Operands: [chargeFees, chargeSupport],
                                },
                            ],
                        },
                    },
                ],
            };
        }
    }
}

function SharedCostOptionsDrawer({ sourcePicker, model }: { sourcePicker: SidePanelOpener; model: SharedCostRuleModel }) {
    const fmtSvc = useDi(FormatService);
    const currencyPrefs = useDi(CurrencyPreferenceService);
    const currency = currencyPrefs.getCurrency();
    const [selections, setSelections] = useState<SharedCostProduct[]>();
    const options = model.getProductOptions();
    const [ready, setReady] = useState<boolean>();
    const loading = useEventValue(model.loading);

    const reset = useCallback(
        (showingPicker: boolean) => {
            if (!showingPicker) {
                setSelections(undefined);
                setReady(false);
                model.clearInitial();
            }
        },
        [selections]
    );

    useEvent(sourcePicker.evt, reset);

    const [gridColumns, state] = useMemo(() => {
        const { lineItemsTypes, productNames } = options.reduce(
            (res, item) => {
                res.lineItemsTypes.add(item.lineItemType);
                res.productNames.add(item.productName);
                return res;
            },
            { lineItemsTypes: new Set<string>(), productNames: new Set<string>() }
        );
        const [litOptions, nameOptions] = [lineItemsTypes, productNames].map((values) =>
            [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map((value) => ({ value, label: value }))
        );
        const columns = [
            {
                accessor: 'lineItemType',
                defaultWidth: 120,
                id: 'lineItemType',
                header: 'Type',
                type: 'string',
                sortField: 'lineItemType',
                noRemove: true,
                filter: {
                    filterType: 'string',
                    filterField: 'lineItemType',
                    name: 'Type',
                    options: {
                        getValueProvider: () => litOptions,
                    },
                },
            },
            {
                accessor: 'productName',
                defaultWidth: 250,
                id: 'productName',
                header: 'Product Name',
                type: 'string',
                sortField: 'productName',
                noRemove: true,
                filter: {
                    filterType: 'string',
                    filterField: 'productName',
                    name: 'Product Name',
                    options: {
                        getValueProvider: () => nameOptions,
                    },
                },
            },
            {
                accessor: 'cost',
                defaultWidth: 150,
                id: 'cost',
                header: 'Amount',
                type: 'number',
                formatter: (v) => fmtSvc.formatMoneyWithCurrency(v.cost ?? 0, currency, 2),
                align: 'right',
                noRemove: true,
                filter: true,
                sortField: 'cost',
            },
        ] as DataColumnConfig<SharedCostProduct>[];

        const state: DataGridState = {
            columns: columns.map((c) => ({ id: c.id, width: c.defaultWidth })),
            filters: [{ Operation: 'ne', Operands: [{ Field: 'cost' }, { Value: 0 }] }],
            sort: [{ Expr: { Field: 'cost' }, Direction: 'Desc' }],
        };

        return [columns, state];
    }, [options]);

    useEffect(() => {
        if (!loading) {
            setSelections(model.getOrCreateInitialSelection());
            setReady(true);
        }
    }, [ready, loading, model]);

    const handleSave = useCallback(() => {
        if (selections) {
            model.setSelectedProducts(selections ?? []);
        }
        sourcePicker.close();
    }, [selections]);

    const handleSelectionChanged = useCallback(async ({ getItems }: { getItems: () => Promise<SharedCostProduct[]> }) => {
        const selections = await getItems();
        setSelections(selections);
    }, []);

    return (
        <SidePanel
            size={650}
            title="Allocation Source"
            opener={sourcePicker}
            toolbar={
                <>
                    <Button onClick={sourcePicker.close} variant="outline">
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleSave}>
                        Save
                    </Button>
                </>
            }
        >
            {() => (
                <FillerSwitch loading={!ready}>
                    {() => (
                        <DataGrid
                            dataSource={model.getProductOptions()}
                            columns={gridColumns}
                            onRowClick="select"
                            selectionMode="multiple"
                            state={state}
                            statePersistence={{ key: 'ShowbackSharedCostPicker' }}
                            onSelectedChanged={handleSelectionChanged}
                            initialSelection={selections}
                            minimumLoadingMs={0}
                            hideColumnSelector
                            hideGlobalSearch
                            hideHeader
                            hideMenu
                        />
                    )}
                </FillerSwitch>
            )}
        </SidePanel>
    );
}

function CustomFeeDrawer({
    setIsCustomOpen,
    handleCustomSave,
}: {
    setIsCustomOpen: SidePanelOpener;
    handleCustomSave: (
        name: string,
        description: string,
        amount: number,
        date: Date,
        amoritizationPeriodValue: number,
        amortizationPeriodUnit: 'Years' | 'Months' | 'Days' | 'None'
    ) => void;
}) {
    const [customAmount, setCustomAmount] = useState(0);
    const [customName, setCustomName] = useState('');
    const [customDescription, setCustomDescription] = useState('');
    const [customDate, setCustomDate] = useState<Date>(new Date());
    const [amortUnit, setAmortUnit] = useState<'Years' | 'Months' | 'Days' | 'None'>('Years');
    const [amortValue, setAmortValue] = useState(0);
    const [canSave, setCanSave] = useState(false);
    useEvent(
        setIsCustomOpen.evt,
        useCallback(() => {
            setCustomAmount(0);
            setCustomName('');
            setCustomDescription('');
            setCustomDate(new Date());
            setAmortUnit('Years');
            setAmortValue(0);
        }, [])
    );

    useEffect(() => {
        setCanSave(customAmount > 0 && customName.length > 0);
    }, [customAmount, customName]);
    return (
        <SidePanel
            opener={setIsCustomOpen}
            size={500}
            title="Custom Fee"
            toolbar={
                <>
                    <Button onClick={setIsCustomOpen.close} variant="outline">
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={() => handleCustomSave(customName, customDescription, customAmount, customDate, amortValue, amortUnit)}
                        disabled={!canSave}
                    >
                        Save
                    </Button>
                </>
            }
        >
            {() => (
                <Stack align="flex-start">
                    <TextInput
                        type="text"
                        name="name"
                        sx={{ width: 320 }}
                        placeholder="Enter name"
                        label="Name"
                        onChange={(e) => setCustomName(e.target.value)}
                    />
                    <TextInput
                        type="text"
                        name="description"
                        placeholder="Enter description"
                        label="Description"
                        sx={{ width: 320 }}
                        onChange={(e) => setCustomDescription(e.target.value)}
                    />
                    <NumberInput
                        name="amount"
                        sx={{ width: 200 }}
                        placeholder="Enter amount"
                        label="Amount"
                        value={customAmount}
                        onChange={(value) => setCustomAmount(value ?? 0)}
                    />
                    <DatePicker
                        sx={{ width: 200 }}
                        label="Date"
                        value={customDate}
                        onChange={(value) => setCustomDate(value ?? new Date())}
                        icon={<CalendarTime />}
                    />

                    <NumberInput
                        name="amount"
                        sx={{ width: 250 }}
                        placeholder="Enter amount"
                        label="Amortize Over"
                        value={amortValue}
                        onChange={(value) => setAmortValue(value ?? 0)}
                        rightSectionWidth={125}
                        rightSection={
                            <Select
                                data={[
                                    { label: 'Years', value: 'Years' },
                                    { label: 'Months', value: 'Months' },
                                    { label: 'Days', value: 'Days' },
                                ]}
                                sx={{ ['input']: { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
                                value={amortUnit}
                                onChange={(value) => setAmortUnit((value as 'Years' | 'Months' | 'Days' | 'None') ?? 'None')}
                            />
                        }
                    />
                </Stack>
            )}
        </SidePanel>
    );
}
